/**
 * MockExam list page (模擬試験の一覧).
 * Lists the active mock exams for the learner's industry. Each card links to
 * that exam's detail page. When the learner has no active exams, shows the
 * "coming soon" empty state.
 */
import { Stack, Typography } from "@mui/material";
import { FC } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { background, black, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { useMockExamList } from "@/infrastructure/adapters/mock-exam";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { AppRoutes } from "@/routes/config";
import { MockExamListItem } from "@/features/mock-exam/components/mock-exam/MockExamListItem";
import { OverviewNoQuestionContainer } from "@/features/mock-exam/components/mock-exam/OverviewNoQuestionContainer";
import { LoadingArea } from "@/components/elements/Loading/Loading";

export const MockExamListPage: FC = () => {
  const { t } = useTranslation();
  const { isTrial, isLoading: isTrialLoading } = useTrialStatus();
  const { mockExams, isLoading } = useMockExamList({ enabled: !isTrialLoading && !isTrial });

  if (isTrialLoading || isLoading) {
    return <LoadingArea />;
  }

  if (isTrial) {
    return <Navigate to={AppRoutes.study} replace />;
  }

  const hasExams = mockExams.length > 0;

  return (
    <Stack flex={1} p={spacing.sm} gap={spacing.sm} bgcolor={background.primary}>
      {hasExams ? (
        <>
          {/* Title */}
          <Stack direction="row" alignItems="center" pb={spacing.smd} borderBottom={`1px solid ${black[95]}`}>
            <Typography variant="sub16Bold" color={text.primary}>
              {t("mockExam.list.title")}
            </Typography>
          </Stack>

          <Stack gap={spacing.sm}>
            {mockExams.map((item) => (
              <MockExamListItem key={item.mockExamId} item={item} />
            ))}
          </Stack>
        </>
      ) : (
        <Stack p={spacing.sm} gap={spacing.sm} bgcolor={background.white} borderRadius={rounded.xs}>
          <Stack minHeight={36} gap={spacing.xs} pb={spacing.smd} borderBottom={`1px solid ${black[95]}`}>
            <Typography variant="sub16Bold" color={text.primary}>{t("mockExam.title")}</Typography>
          </Stack>

          <OverviewNoQuestionContainer />
        </Stack>
      )}
    </Stack>
  );
};

export default MockExamListPage;
